/**
 * The glossary's shape.
 *
 * Josh, 2026-09-05: "as comprehensive as possible of as many commercial
 * insurance terms and phrases as possible. This portal is to be used by brand
 * new to commercial agents and seasoned vets."
 *
 * ⚠️ THOSE TWO READERS WANT DIFFERENT THINGS FROM THE SAME ENTRY, WHICH IS WHY
 * `def` AND `detail` ARE SEPARATE FIELDS. A new agent needs one plain sentence
 * that lets them carry on reading the email. A twenty-year producer already
 * knows what an aggregate is and is looking up the bit that bites — that a
 * per-project aggregate has to be endorsed on, that it does not restore at
 * renewal for a claims-made policy. Folding both into one paragraph produces an
 * entry that is too long for the first reader and too shallow for the second.
 *
 * ⚠️ `confused` IS THE FIELD THAT EARNS THE PAGE. Almost nothing in commercial
 * insurance is hard because the definition is hard; it is hard because two
 * terms sound alike and mean different things, and nobody tells you which pairs
 * those are. Additional insured and loss payee. Umbrella and excess. Waiver of
 * subrogation and hold harmless. A glossary that defines each of those
 * perfectly and never puts them side by side has left the actual work undone.
 */
/**
 * ⚠️ ALPHABETICAL BY LABEL, AND SORTED HERE SO EVERY SURFACE AGREES (Josh,
 * 2026-09-05: "make thes in the left part in alphabetical order"). It was
 * written in a thematic order — foundations, then policy, then the specialist
 * areas — which is a good order for reading the file and a bad one for finding
 * "Claims" in a list of sixteen. The declaration order below is left thematic
 * because it documents how the categories relate; the sort is what ships.
 */
const CATEGORIES_BY_THEME = [
    { id: 'foundations', label: 'Foundations', blurb: 'The ideas everything else is built on.' },
    { id: 'policy', label: 'Policy anatomy', blurb: 'The parts of a policy and what each one does.' },
    { id: 'limits', label: 'Limits & deductibles', blurb: 'How much is covered, and what you pay first.' },
    { id: 'lines', label: 'Lines & coverages', blurb: 'The policies themselves, and what they buy.' },
    { id: 'liability', label: 'Liability detail', blurb: 'The clauses that decide who is covered for what.' },
    { id: 'property', label: 'Property detail', blurb: 'Valuation, perils, and how a building is described.' },
    { id: 'workcomp', label: "Workers' comp", blurb: 'Its own vocabulary, shared with almost nothing else.' },
    { id: 'auto', label: 'Auto & trucking', blurb: 'Symbols, filings, and the language of fleets.' },
    { id: 'professional', label: 'Professional & management', blurb: 'E&O, D&O, EPLI, cyber and crime.' },
    { id: 'underwriting', label: 'Underwriting & rating', blurb: 'How a carrier decides, and how the price is built.' },
    { id: 'market', label: 'Markets & distribution', blurb: 'Who carries the paper and who sits between you and it.' },
    { id: 'money', label: 'Premium & billing', blurb: 'What gets charged, earned, returned and financed.' },
    { id: 'claims', label: 'Claims', blurb: 'From the first notice to the closing payment.' },
    { id: 'documents', label: 'Forms & documents', blurb: 'The paper that moves an account along.' },
    { id: 'practice', label: 'Agency practice', blurb: 'How the work actually gets done in an agency.' },
    { id: 'regulatory', label: 'Licensing & regulation', blurb: 'The rules you personally are bound by.' },
];
export const GLOSSARY_CATEGORIES = [...CATEGORIES_BY_THEME].sort((a, b) => a.label.localeCompare(b.label, 'en', { sensitivity: 'base' }));
export const CATEGORY_LABEL = Object.fromEntries(GLOSSARY_CATEGORIES.map((c) => [c.id, c.label]));
